import { Directive, ElementRef, HostListener, AfterViewInit, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Directiva para formatear automáticamente valores como moneda CLP
 * Formatea en tiempo real mientras escribes
 * Uso: <input appCurrencyFormat [formControlName]="controlName" />
 * 
 * Características:
 * - Formatea instantáneamente mientras escribes
 * - Separa con puntos cada 3 dígitos (1.000.000)
 * - Formatea valores precargados automáticamente
 * - Mantiene el valor numérico en el control del formulario
 * - Solo acepta dígitos
 * - Mantiene la posición del cursor correctamente
 */
@Directive({
  selector: '[appCurrencyFormat]',
  standalone: true
})
export class CurrencyFormatDirective implements AfterViewInit {
  @Input() maxAmount: number | null = null;
  /** Si es false, permite valores sobre el máximo (para mostrar error de validación en UI). */
  @Input() clampToMax = true;
  
  constructor(
    private readonly el: ElementRef,
    private readonly ngControl: NgControl
  ) {}

  /**
   * Al inicializar la vista, formatear valor inicial si existe
   */
  ngAfterViewInit(): void {
    this.formatInitialValue();
  }

  /**
   * Formatea el valor inicial del input
   */
  private formatInitialValue(): void {
    const currentValue = this.el.nativeElement.value;
    if (currentValue) {
      const digitsOnly = currentValue.replace(/\D/g, '');
      if (digitsOnly) {
        const numValue = Number.parseInt(digitsOnly, 10);
        const formatted = this.formatCurrency(numValue);
        this.el.nativeElement.value = formatted;
      }
    }
  }

  /**
   * Al escribir en el input, formatea instantáneamente (solo visual)
   * NO actualiza el FormControl para evitar re-renders que pierdan el foco
   */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPos = input.selectionStart || 0;
    
    // Extraer solo dígitos
    const digitsOnly = input.value.replace(/\D/g, '');

    if (digitsOnly) {
      let numValue = Number.parseInt(digitsOnly, 10);
      
      // Si se definió máximo y se supera, recortar dígito por dígito
      if (this.clampToMax && this.maxAmount !== null && numValue > this.maxAmount) {
        while (numValue > this.maxAmount && numValue > 0) {
          numValue = Math.floor(numValue / 10);
        }
      }
      
      const formatted = this.formatCurrency(numValue);
      
      // Calcular nueva posición del cursor
      const newCursorPos = this.calculateCursorPosition(String(numValue), formatted, cursorPos);
      
      input.value = formatted;
      
      // Establecer cursor en la posición correcta
      setTimeout(() => {
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
      
      // NO actualizar el control aquí - solo formatear visualmente
      // El control se actualiza en blur
    } else {
      input.value = '';
    }
  }

  /**
   * Al perder el foco, asegurar que el formato es correcto y actualizar el control
   */
  @HostListener('blur')
  onBlur(): void {
    const value = this.el.nativeElement.value.replace(/\D/g, '');
    if (value) {
      let numValue = Number.parseInt(value, 10);
      
      // Validar nuevamente contra máximo en blur (respaldo)
      if (this.clampToMax && this.maxAmount !== null && numValue > this.maxAmount) {
        while (numValue > this.maxAmount && numValue > 0) {
          numValue = Math.floor(numValue / 10);
        }
      }
      
      const formatted = this.formatCurrency(numValue);
      // Actualizar el control con el valor numérico en blur
      this.ngControl.control?.setValue(numValue, { emitEvent: false });
      // Reaplicar formato al final del ciclo para evitar que Angular lo reescriba como "1000"
      queueMicrotask(() => {
        this.el.nativeElement.value = formatted;
      });
    } else {
      this.ngControl.control?.setValue(null, { emitEvent: false });
      queueMicrotask(() => {
        const current = this.ngControl.control?.value;
        const numericCurrent = typeof current === 'number' ? current : Number(current);
        if (current !== null && current !== undefined && current !== '' && !Number.isNaN(numericCurrent) && numericCurrent > 0) {
          this.el.nativeElement.value = this.formatCurrency(numericCurrent);
          return;
        }
        this.el.nativeElement.value = '';
      });
    }
  }

  /**
   * Calcula la posición correcta del cursor después de formatear
   * @param digitsOnly Solo los dígitos del valor
   * @param formatted Valor formateado
   * @param cursorPos Posición actual del cursor
   */
  private calculateCursorPosition(
    digitsOnly: string,
    formatted: string,
    cursorPos: number
  ): number {
    // Contar cuántos dígitos hay antes de la posición del cursor
    let digitCount = 0;
    
    // Buscar en el valor formateado, la posición correspondiente
    for (let i = 0; i < formatted.length; i++) {
      const char = formatted[i];
      
      // Solo contar dígitos (excluir $, puntos, espacios)
      if (/\d/.exec(char)) {
        digitCount++;
        
        // Si hemos llegado a la cantidad de dígitos que debería haber
        if (digitCount >= cursorPos) {
          return i + 1;
        }
      }
    }
    
    // Si no encontramos, retornar el final de la cadena
    return formatted.length;
  }

  /**
   * Formatea un número como moneda CLP
   * @param value Valor numérico
   * @returns Valor formateado (ej: $ 1.000.000)
   */
  private formatCurrency(value: number): string {
    if (!value || value === 0) return '';

    const formatted = new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);

    return `$ ${formatted}`;
  }
}